import { useState } from 'react';
import { formatCents } from '@affily/shared/money';
import { api } from '../lib/api.js';
import { Badge, Button } from './ui.jsx';
import { HelpLink } from '../routes/Help.jsx';

const earnedTone = { approved: 'positive', settled: 'positive', paid: 'positive', pending: 'neutral', reversed: 'negative' };

/**
 * What a creator's referrals did on one partnership: each viewing booked,
 * sign-up or sale, as it happened, and what it earned. Anonymised — the date,
 * the action and the money, never the customer or their order.
 *
 * Collapsed until asked for, so a page of partnerships isn't a page of calls.
 */
export function PartnershipActivity({ partnershipId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function load(before = null) {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(
        `/dashboard/creator/activity?partnershipId=${partnershipId}&limit=15${before ? `&before=${before}` : ''}`,
      );
      setRows((r) => (before ? [...r, ...res.activity] : res.activity));
      setHasMore(res.hasMore);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="border-t border-rule p-4">
      <button
        type="button"
        onClick={() => {
          if (!open && rows.length === 0) load();
          setOpen(!open);
        }}
        className="text-2xs font-semibold uppercase tracking-wider text-ink-faint hover:text-ink"
        aria-expanded={open}
      >
        {open ? '▾' : '▸'} What your referrals did
      </button>

      {open && (
        <div className="mt-2">
          {error && <p className="text-xs text-negative">{error}</p>}
          {!loading && rows.length === 0 && !error && (
            <p className="text-sm text-ink-muted">
              Nothing yet. Each booking, sign-up or sale your link or code brings shows up here as it
              happens.
            </p>
          )}
          <ul className="divide-y divide-rule">
            {rows.map((a) => (
              <li key={a.id} className="flex flex-wrap items-baseline justify-between gap-2 py-1.5 text-sm">
                <span>
                  <span className="font-medium">{a.eventName}</span>
                  <span className="ml-2 text-xs text-ink-faint">
                    {new Date(a.occurredAt).toLocaleDateString()} · via your {a.via}
                  </span>
                  {a.from && (
                    <span className="block text-xs text-ink-muted">
                      From the {a.from.eventName.toLowerCase()} on{' '}
                      {new Date(a.from.occurredAt).toLocaleDateString()}
                    </span>
                  )}
                  {a.note && <span className="block text-xs text-ink-faint">{a.note}</span>}
                  {a.orderState === 'awaiting_payment' && (
                    <span className="block text-xs text-ink-faint">
                      It earns once the customer pays.
                    </span>
                  )}
                  {a.orderState === 'refunded' && (
                    <span className="block text-xs text-ink-faint">The customer was refunded.</span>
                  )}
                </span>
                {a.orderState === 'awaiting_payment' ? (
                  <Badge>awaiting payment</Badge>
                ) : a.orderState === 'cancelled' ? (
                  <Badge tone="negative">order cancelled</Badge>
                ) : a.earned ? (
                  <Badge tone={earnedTone[a.earned.status] ?? 'neutral'}>
                    {formatCents(a.earned.netAmountCents, { currency: a.earned.currency })} · {a.earned.status}
                  </Badge>
                ) : (
                  <Badge>tracked</Badge>
                )}
              </li>
            ))}
          </ul>
          {hasMore && (
            <Button size="sm" variant="ghost" onClick={() => load(rows[rows.length - 1].id)} disabled={loading}>
              Older
            </Button>
          )}
          {loading && <p className="text-xs text-ink-faint">Loading…</p>}
          <p className="text-xs text-ink-faint">
            Something missing? <HelpLink slug="why-wasnt-my-sale-credited">Why a sale might not be credited</HelpLink>
          </p>
        </div>
      )}
    </div>
  );
}
